// #region imports

// --- Angular
import { Router } from '@angular/router';

// --- RxJs
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { User as OidcUser } from 'oidc-client';
import { ReCaptchaV3Service } from 'ngx-captcha';

// --- HS Code
import { AppService } from '../../core/services/app.service';
import { OidcService, LanguagesBaseService, RegistrationBaseService } from '../../core/services';
import { LanguageModel, RegistrationModel, EmailAddressValidationModel, Constants, RegistrationResult } from '../../models';
import { environment } from '../../../environments/environment';

// #endregion
export abstract class RegistrationBase {
  errorMessage$: Observable<string>;

  isRegistering$: Observable<boolean>;
  isRegistered$: Observable<boolean>;
  isEmailAddressValidated$: Observable<EmailAddressValidationModel>;
  isValidatingEmailAddress$: Observable<boolean>;

  languages$: Observable<LanguageModel[]>;
  selectedLanguage$: Observable<LanguageModel>;

  emailAddress$: Observable<string>;
  _emailAddress: string;

  firstNameRequiredMessage$: Observable<string>;
  firstNameMaxLengthdMessage$: Observable<string>;
  lastNameRequiredMessage$: Observable<string>;
  lastNameMaxLengthdMessage$: Observable<string>;
  passwordRequiredMessage$: Observable<string>;
  passwordMinLengthMessage$: Observable<string>;
  passwordMaxLengthMessage$: Observable<string>;
  passwordComplexityMessage$: Observable<string>;
  confirmPasswordRequiredMessage$: Observable<string>;
  confirmPasswordMatchMessage$: Observable<string>;
  emailAddressRequiredMessage$: Observable<string>;
  emailAddressMaxLengthMessage$: Observable<string>;
  emailAddressInvalidMessage$: Observable<string>;
  isbnNumberRequiredMessage$: Observable<string>;
  isbnNumberMaxLengthMessage$: Observable<string>;
  userAgreementRequiredMessage$: Observable<string>;
  captchaRequiredMessage$: Observable<string>;
  registrationResult$: Observable<RegistrationResult>;

  isRegistered: boolean;

  bookUrl = Constants.BOOK_URL;

  private _identitySubscription: Subscription;

  constructor(
    protected router: Router,
    protected registrationService: RegistrationBaseService,
    protected appService: AppService,
    protected oidcService: OidcService,
    protected languageService: LanguagesBaseService,
    protected translate: TranslateService,
    protected reCaptchaV3Service: ReCaptchaV3Service
  ) {

    this.errorMessage$ = new BehaviorSubject<string>(null);
    this.isEmailAddressValidated$ = of({
      isEmailAddressValidated: false,
      isValidEmailAddress: false,
      emailAddressExists: false
    } as EmailAddressValidationModel);
  }

  protected _onInit(): void {
    this.isRegistering$ = this.registrationService.isRegistering$;
    this.isRegistered$ = this.registrationService.isRegistered$;
    this.isValidatingEmailAddress$ = this.registrationService.isValidatingEmailAddress$;
    this.isEmailAddressValidated$ = this.registrationService.isEmailAddressInUse$;
    this.emailAddress$ = this.registrationService.emailAddress$;
    this.errorMessage$ = this.registrationService.registrationError$;
    this.registrationResult$ = this.registrationService.registrationResult$;

    this.appService.setLoading(true);
    this._identitySubscription = this.oidcService.identity$.subscribe((identity: OidcUser) => {
      this.appService.setLoading(false);
      if (identity) {
        this.router.navigate(['/']);
      }
    });

    this.selectedLanguage$ = this.languageService.selectedLanguage$;
    this.languages$ = this.languageService.languages$;
    this.oidcService.getOidcUser();

    this.firstNameRequiredMessage$ = this.translate
      .get('firstName')
      .pipe(
        switchMap(firstName =>
          this.translate.get('validationMessageFieldRequired', {
            value: firstName
          })
        )
      );

    this.firstNameMaxLengthdMessage$ = this.translate
      .get('firstName')
      .pipe(
        switchMap(firstName =>
          this.translate.get('validationMessageFieldMaxLength', {
            value: firstName,
            x: Constants.FIRST_NAME_MAXIMUM_LENGTH
          })
        )
      );

    this.lastNameRequiredMessage$ = this.translate
      .get('lastName')
      .pipe(
        switchMap(lastName =>
          this.translate.get('validationMessageFieldRequired', {
            value: lastName
          })
        )
      );

    this.lastNameMaxLengthdMessage$ = this.translate
      .get('lastName')
      .pipe(
        switchMap(lastName =>
          this.translate.get('validationMessageFieldMaxLength', {
            value: lastName,
            x: Constants.LAST_NAME_MAXIMUM_LENGTH
          })
        )
      );

    this.passwordRequiredMessage$ = this.translate
      .get('password')
      .pipe(
        switchMap(password =>
          this.translate.get('validationMessageFieldRequired', {
            value: password
          })
        )
      );

    this.passwordMinLengthMessage$ = this.translate
      .get('password')
      .pipe(
        switchMap(password =>
          this.translate.get('validationMessageFieldMinLength', {
            value: password,
            x: Constants.PASSWORD_MINIMUM_LENGTH
          })
        )
      );

    this.passwordMaxLengthMessage$ = this.translate
      .get('password')
      .pipe(
        switchMap(password =>
          this.translate.get('validationMessageFieldMaxLength', {
            value: password,
            x: Constants.PASSWORD_MAXIMUM_LENGTH
          })
        )
      );

    this.passwordComplexityMessage$ = this.translate
      .get('password')
      .pipe(
        switchMap(password =>
          this.translate.get('validationMessageComplexity', {
            value: password
          })
        )
      );

    this.confirmPasswordRequiredMessage$ = this.translate
      .get('confirmPassword')
      .pipe(
        switchMap(confirmPassword =>
          this.translate.get('validationMessageFieldRequired', {
            value: confirmPassword
          })
        )
      );

    this.confirmPasswordMatchMessage$ = this.translate
      .get('confirmPassword')
      .pipe(
        switchMap(confirmPassword =>
          this.translate.get('validationMessageMatch', {
            value: confirmPassword
          })
        )
      );

    this.emailAddressRequiredMessage$ = this.translate
      .get('emailAddress')
      .pipe(
        switchMap(emailAddress =>
          this.translate.get('validationMessageFieldRequired', {
            value: emailAddress
          })
        )
      );

    this.emailAddressMaxLengthMessage$ = this.translate
      .get('emailAddress')
      .pipe(
        switchMap(emailAddress =>
          this.translate.get('validationMessageFieldMaxLength', {
            value: emailAddress,
            x: Constants.EMAIL_ADDRESS_MAXIMUM_LENGTH
          })
        )
      );

    this.emailAddressInvalidMessage$ = this.translate
      .get('emailAddress')
      .pipe(
        switchMap(emailAddress =>
          this.translate.get('validationMessageInvalid', {
            value: emailAddress
          })
        )
      );

    this.isbnNumberRequiredMessage$ = this.translate
      .get('isbnNumber')
      .pipe(
        switchMap(isbnNumber =>
          this.translate.get('validationMessageFieldRequired', {
            value: isbnNumber
          })
        )
      );

    this.isbnNumberMaxLengthMessage$ = this.translate
      .get('isbnNumber')
      .pipe(
        switchMap(isbnNumber =>
          this.translate.get('validationMessageFieldMaxLength', {
            value: isbnNumber,
            x: Constants.ISBN_NUMBER_MAXIMUM_LENGTH
          })
        )
      );

    this.userAgreementRequiredMessage$ = this.translate
      .get('validationMessageTermsRequired');

    this.captchaRequiredMessage$ = this.translate
      .get('validationMessageCaptchaRequired');

    this.isRegistered$.subscribe((registered: boolean) => {
      this.isRegistered = registered;
    });
  }

  protected _onDestroy(): void {
    if (this._identitySubscription) {
      this._identitySubscription.unsubscribe();
      this._identitySubscription = null;
    }
  }

  onRegister(e: RegistrationModel) {
    this.appService.setLoading(true);
    this.reCaptchaV3Service.execute(environment.siteKey, 'register', (token) => {
      e.recaptcha = token;
      this.appService.setLoading(false);
      this.registrationService.registerNewUser(e);
    }, {
      useGlobalDomain: false // optional
    });
  }

  onCancel() {
    this.router.navigate(['/']);
  }

  onLanguageChanged(newLanguage: LanguageModel): void {
    const translateLanguageAndCulture = newLanguage.cultureCode
      ? `${newLanguage.isoTwoLetterName}-${newLanguage.cultureCode}`
      : `${newLanguage.isoTwoLetterName}`;
    this.languageService.setSelectedLanguage(newLanguage);
    this.translate.use(translateLanguageAndCulture);
  }

  onLogin(): void {
    this.appService.setLoading(true);
    this.oidcService.signInRedirect();
  }

  onEmailAddressChanged(emailAddress: string): void {
    this.registrationService.checkIfEmailAddressInUse(emailAddress);
  }

  onResend(emailAddress: string) {
    if (!emailAddress) {
      return;
    }
    this.registrationService.resendRegistrationEmail(emailAddress);
  }
}
